import { useCallback, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { BoxArrowUpRight } from 'react-bootstrap-icons';
import { Col, Container, Row } from 'react-bootstrap';
import { useBootstrapPrefix } from 'react-bootstrap/ThemeProvider';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { AsyncPage, SearchInput } from '@/components';
import { createUseThemedStyles } from '@/hooks';
import { ToolModel } from '@/lib/models';
import { toolsService } from '@/lib/services';

const useStyles = createUseThemedStyles((theme) => ({
	content: {
		maxWidth: 900,
		margin: '0 auto',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 24,
		marginBottom: 32,
		'@media (max-width: 575px)': {
			alignItems: 'flex-start',
			flexDirection: 'column',
		},
	},
	title: {
		margin: 0,
		color: theme.colors.gray800,
		...theme.fonts.l,
		...theme.fonts.openSansSemiBold,
	},
	search: {
		width: '100%',
		maxWidth: 430,
	},
	toolList: {
		borderTop: 0,
	},
	tool: {
		padding: '28px 0',
		borderBottom: `1px solid ${theme.colors.gray300}`,
	},
	toolWithThumbnail: {
		display: 'grid',
		gap: 24,
		gridTemplateColumns: 'minmax(0, 1fr) 290px',
		'@media (max-width: 575px)': {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	toolTitle: {
		marginBottom: 6,
		color: theme.colors.gray900,
		...theme.fonts.m,
		...theme.fonts.openSansSemiBold,
	},
	toolVersion: {
		marginBottom: 12,
		color: theme.colors.gray700,
		...theme.fonts.s,
	},
	toolDescription: {
		marginBottom: 16,
		display: '-webkit-box',
		overflow: 'hidden',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 3,
		...theme.fonts.s,
	},
	toolActions: {
		display: 'flex',
		gap: 16,
		alignItems: 'center',
	},
	openButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		'&&': {
			padding: '5px 14px',
			...theme.fonts.xs,
			...theme.fonts.openSansBold,
		},
	},
	thumbnail: {
		width: '100%',
		height: 128,
		alignSelf: 'center',
		objectFit: 'cover',
		borderRadius: 4,
		backgroundColor: theme.colors.gray300,
		'@media (max-width: 575px)': {
			maxWidth: 290,
		},
	},
	emptyState: {
		padding: '52px 0',
		textAlign: 'center',
		color: theme.colors.gray600,
	},
}));

const describePublishedVersion = (tool: ToolModel) =>
	`${tool.versionLabel} - ${format(new Date(tool.publishedAt), 'MMMM d, yyyy')}`;

export const Tools = () => {
	const classes = useStyles();
	const buttonPrefix = useBootstrapPrefix(undefined, 'btn');
	const [tools, setTools] = useState<ToolModel[]>([]);
	const [search, setSearch] = useState('');

	const fetchTools = useCallback(async () => {
		const response = await toolsService.getTools().fetch();
		setTools(response.tools);
	}, []);

	const filteredTools = useMemo(() => {
		const normalizedSearch = search.trim().toLocaleLowerCase();
		if (!normalizedSearch) {
			return tools;
		}
		return tools.filter((tool) => tool.title.toLocaleLowerCase().includes(normalizedSearch));
	}, [search, tools]);

	return (
		<>
			<Helmet>
				<title>Hackett Connect - Accelerator Library</title>
			</Helmet>
			<Container className="py-13">
				<Row>
					<Col>
						<div className={classes.content}>
							<div className={classes.header}>
								<h1 className={classes.title}>Accelerator Library</h1>
								<SearchInput
									className={classes.search}
									aria-label="Search accelerators"
									placeholder="Search by name"
									value={search}
									onChange={({ currentTarget }) => setSearch(currentTarget.value)}
								/>
							</div>
						</div>
					</Col>
				</Row>
				<Row>
					<Col>
						<AsyncPage fetchData={fetchTools}>
							<div className={classes.content}>
								<div className={classes.toolList}>
									{filteredTools.length === 0 ? (
										<p className={classes.emptyState}>No accelerators match your search.</p>
									) : (
										filteredTools.map((tool) => (
											<article
												key={tool.toolId}
												className={`${classes.tool} ${classes.toolWithThumbnail}`}
											>
												<div>
													<h2 className={classes.toolTitle}>
														<Link to={`/tools/${tool.toolId}`}>{tool.title}</Link>
													</h2>
													<p className={classes.toolVersion}>
														{describePublishedVersion(tool)}
													</p>
													{tool.descriptionText && (
														<p className={classes.toolDescription}>
															{tool.descriptionText}
														</p>
													)}
													<div className={classes.toolActions}>
														<Link
															to={`/tools/${tool.toolId}`}
															className={`${buttonPrefix} ${buttonPrefix}-outline-primary ${classes.openButton}`}
														>
															Open Accelerator{' '}
															<BoxArrowUpRight aria-hidden="true" size={16} />
														</Link>
													</div>
												</div>
												{tool.thumbnailUrl ? (
													<img className={classes.thumbnail} src={tool.thumbnailUrl} alt="" />
												) : (
													<div className={classes.thumbnail} aria-hidden="true" />
												)}
											</article>
										))
									)}
								</div>
							</div>
						</AsyncPage>
					</Col>
				</Row>
			</Container>
		</>
	);
};
